import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import JsonLd from "@/components/base/JsonLd";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { posts } from "@/mocks/posts";
import PostActions from "./components/PostActions";
import PostSidebar from "./components/PostSidebar";

const ORIGIN = "https://ymw88.top";

export default function PostDetail() {
  const { id } = useParams();
  const post = posts.find((p) => p.id === id);

  useSeo({
    title: post ? `${post.title} - 黑吃瓜网` : "爆料详情 - 黑吃瓜网",
    description: post
      ? post.excerpt.slice(0, 130)
      : "黑吃瓜网爆料详情页，整理话题脉络、要点速览与后续进展。",
    keywords: post
      ? `${post.tags.join(",")},黑吃瓜网,今日爆料`
      : "黑吃瓜网,爆料详情",
    path: `/post/${id ?? ""}`,
    type: "article",
  });

  if (!post) {
    return (
      <section className="w-full px-4 md:px-8 py-24">
        <div className="max-w-xl mx-auto text-center">
          <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
            <i className="ri-error-warning-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="font-heading font-bold text-2xl text-foreground-950">这条爆料不存在或已下线</h1>
          <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
            可能是链接有误，或者该话题已被合并整理。回到爆料列表看看今天的新瓜吧。
          </p>
          <Link
            to="/breaking"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回爆料列表
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </section>
    );
  }

  const related = posts.filter((p) => p.categorySlug === post.categorySlug && p.id !== post.id).slice(0, 3);
  const fallbackRelated = posts.filter((p) => p.id !== post.id).slice(0, 3);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      image: post.image,
      datePublished: post.date,
      dateModified: "2026-09-10",
      inLanguage: "zh-CN",
      articleSection: post.category,
      keywords: post.tags.join(","),
      author: { "@type": "Person", name: post.author },
      publisher: { "@type": "Organization", name: "黑吃瓜网" },
      mainEntityOfPage: { "@type": "WebPage", "@id": `${ORIGIN}/post/${post.id}` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "今日爆料", item: `${ORIGIN}/breaking` },
        { "@type": "ListItem", position: 3, name: post.title, item: `${ORIGIN}/post/${post.id}` },
      ],
    },
  ];

  return (
    <>
      <JsonLd data={jsonLd} />

      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "今日爆料", to: "/breaking" },
              { label: post.category, to: `/breaking?cat=${post.categorySlug}` },
              { label: "正文" },
            ]}
          />
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-[11px] px-2.5 py-1 rounded-full bg-accent-100 text-accent-800 font-bold">
              {post.category}
            </span>
            <span className="text-[11px] px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 font-medium">
              话题整理
            </span>
          </div>
          <h1 className="mt-4 font-heading font-bold text-2xl md:text-4xl text-foreground-950 leading-snug max-w-4xl">
            {post.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-600">
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-user-3-line" aria-hidden="true"></i>
              {post.author}
            </span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-calendar-line" aria-hidden="true"></i>
              {post.dateLabel}
            </span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-time-line" aria-hidden="true"></i>
              阅读约 {post.readTime}
            </span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-fire-line text-accent-500" aria-hidden="true"></i>
              热度 {post.heat}
            </span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-chat-3-line" aria-hidden="true"></i>
              {post.comments} 条讨论
            </span>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="rounded-lg overflow-hidden border border-background-200 mb-8 md:mb-10">
            <div className="w-full h-56 md:h-[420px] bg-background-200">
              <img
                src={post.image}
                alt={post.imageAlt}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
            <article className="lg:col-span-8">
              <p className="text-base md:text-lg text-foreground-800 leading-relaxed font-medium">
                {post.excerpt}
              </p>

              <PostActions title={post.title} />

              <div className="mt-6 p-5 rounded-lg bg-accent-50 border border-accent-200">
                <h2 className="font-heading font-bold text-base text-accent-900 flex items-center gap-2">
                  <i className="ri-list-check-2" aria-hidden="true"></i>
                  话题要点速览
                </h2>
                <ul className="mt-3 space-y-2.5">
                  {post.keyPoints.map((point) => (
                    <li key={point} className="flex items-start gap-2.5 text-sm text-accent-900 leading-relaxed">
                      <i className="ri-checkbox-blank-circle-fill text-[6px] mt-2 shrink-0" aria-hidden="true"></i>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="post-body mt-8">
                {post.sections.map((section) => (
                  <div key={section.heading}>
                    <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mt-8 mb-3">
                      {section.heading}
                    </h2>
                    {section.paragraphs.map((p) => (
                      <p key={p.slice(0, 24)}>{p}</p>
                    ))}
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500 mr-1">话题标签</span>
                {post.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/breaking?q=${encodeURIComponent(tag)}`}
                    className="text-xs px-3 py-1 rounded-full bg-background-100 border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-6 p-4 rounded-lg bg-background-100 border border-background-200">
                <p className="text-xs text-foreground-600 leading-relaxed">
                  <strong className="text-foreground-800">内容说明：</strong>
                  本文基于公开信息与网友讨论热度整理，涉及尚未证实的说法已在文中标注为讨论方向，不代表本站立场。如发现事实偏差，欢迎通过联系我们页面提交更正。
                </p>
              </div>
            </article>

            <PostSidebar related={relatedList} />
          </div>

          <div className="mt-12">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading font-bold text-2xl text-foreground-950">继续吃瓜</h2>
              <Link
                to="/breaking"
                className="text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                全部爆料 →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedList.map((item) => (
                <Link
                  key={item.id}
                  to={`/post/${item.id}`}
                  className="group flex gap-4 p-4 rounded-lg border border-background-200 bg-background-100 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="w-24 h-20 shrink-0 rounded-md overflow-hidden bg-background-200">
                    <img
                      src={item.image}
                      alt={item.imageAlt}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                      {item.title}
                    </h3>
                    <span className="mt-2 block text-xs text-foreground-500">{item.dateLabel} · {item.category}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}